import { z } from 'zod';
import { IsoTimestamp, QuestionId, QuizId, SchoolId, SessionId, UserId, } from './common.js';
import { IntegrityPactSnapshot, QuizSchedule, StudentQuestion, QUIZ_OBSERVER_MAX } from './quiz.js';
import { ResultDeliveryScoreSnapshot } from './result-delivery.js';
export const SessionStatus = z.enum([
    'ready',
    'inProgress',
    'paused',
    'submitted',
    'graded',
    'flagged',
    'invalidated',
]);
export const SessionOrdering = z.object({
    questionOrder: z.array(QuestionId),
    optionOrder: z.record(QuestionId, z.array(z.string().max(8))),
});
export const SessionScheduleSnapshot = z.object({
    version: z.literal(1),
    classroomId: z.string().min(1).max(64).nullable(),
    schedule: QuizSchedule,
    observerUserIds: z.array(UserId).max(QUIZ_OBSERVER_MAX),
    sourceScheduleId: z.string().min(1).max(160).optional(),
    source: z.object({
        system: z.enum(['aimsis', 'cbt']),
        examId: z.string().min(1).max(64).optional(),
        syncedAt: IsoTimestamp.optional(),
    }).optional(),
});
export const CameraConsentRequest = z.object({
    consentVersion: z.string().trim().min(1).max(32),
    /**
     * `withdrawn` is a mid-attempt withdrawal (AIMSIS-20724): the stream stops and
     * every later capture is refused with `consent_withdrawn`. It is distinct from
     * `declined`, which is the answer at the consent gate before any capture.
     */
    decision: z.enum(['granted', 'declined', 'withdrawn']),
});
export const CameraConsentSnapshot = CameraConsentRequest.extend({
    mode: z.enum(['optional', 'required']),
    timestamp: IsoTimestamp,
});
/**
 * What the student's device actually reported about the camera, as opposed to
 * the consent decision. `active` means a live track was available; the others
 * distinguish a browser block (`blocked`/`denied`) from a track that was lost
 * after it worked (`revoked`) and from a device that simply has no camera.
 */
export const CameraAvailabilityStatus = z.enum([
    'active',
    'blocked',
    'denied',
    'revoked',
    'unavailable',
]);
/** Server-stamped availability report. `detectedAt` is never taken from the client. */
export const CameraAvailabilitySnapshot = z.object({
    status: CameraAvailabilityStatus,
    detectedAt: IsoTimestamp,
});
export const Session = z.object({
    id: SessionId,
    schoolId: SchoolId,
    quizId: QuizId,
    participantId: UserId,
    status: SessionStatus,
    lifecycleState: SessionStatus.optional(),
    version: z.number().int().nonnegative().default(0),
    attemptNumber: z.number().int().positive().default(1),
    lineageRootSessionId: SessionId.optional(),
    lineageParentSessionId: SessionId.optional(),
    lineageCommandId: z.string().uuid().optional(),
    lineageRelation: z.enum(['reset', 'reattempt']).optional(),
    startedAt: IsoTimestamp,
    lastHeartbeatAt: IsoTimestamp.optional(),
    roomBroadcast: z.object({
        id: z.string().uuid(),
        message: z.string().max(500),
        sentAt: IsoTimestamp,
        sentBy: UserId,
    }).optional(),
    submittedAt: IsoTimestamp.optional(),
    deviceFingerprint: z.string().max(256).optional(),
    clientTimeOffsetMs: z.number().int(), // server - client at session start
    ordering: SessionOrdering,
    questionSnapshot: z.array(StudentQuestion).max(200).optional(),
    scheduleSnapshot: SessionScheduleSnapshot.optional(),
    totalScore: z.number().nonnegative().optional(),
    autoScore: z.number().nonnegative().optional(),
    manualScore: z.number().nonnegative().optional(),
    maxScore: z.number().nonnegative().optional(),
    autoScored: z.boolean().default(false),
    manualScored: z.boolean().default(false),
    integrityPact: IntegrityPactSnapshot.optional(),
    /**
     * The quiz's `rules.requireLockdownClient` as it stood when this attempt was
     * created (AIMSIS-20925). Frozen on the session so the student endpoints
     * enforce the requirement the attempt actually started under: an author who
     * flips the quiz rule mid-exam cannot lock a running attempt out of its own
     * answers, and cannot silently open one that was started in the app.
     *
     * `/sessions/start` admits a RESUMED attempt on this value too, but admits a
     * NEW one on the quiz's live rule — an attempt that does not exist yet has no
     * snapshot to honour. A make-up retake is a new attempt even when the attempt
     * it replaces is still non-terminal (it predates the governing window), so it
     * follows the live rule as well. Replacement attempts created by recovery copy
     * this value from their source attempt instead, because a recovery continues
     * that attempt's lineage rather than starting a new one.
     *
     * Absent on sessions created before the rule existed, which read as not
     * required — the pre-existing behaviour.
     */
    lockdownClientRequired: z.boolean().optional(),
    cameraConsent: CameraConsentSnapshot.optional(),
    cameraAvailability: CameraAvailabilitySnapshot.optional(),
    resultDeliverySnapshot: ResultDeliveryScoreSnapshot.optional(),
    resultDeliveryStatus: z.enum(['not_applicable', 'pending', 'processing', 'retrying', 'delivered', 'failed', 'accepted', 'already-applied']).optional(),
    resultDeliveryId: z.string().max(128).optional(),
    resultDeliveryVersion: z.number().int().positive().optional(),
    resultDeliveryExternalId: z.string().max(128).optional(),
    resultDeliveryPredecessorId: z.string().max(128).optional(),
    resultCorrectionOfSessionId: SessionId.optional(),
    resultCorrectionCommandId: z.string().uuid().optional(),
    resultCorrectionRequestedVersion: z.number().int().positive().optional(),
    resultReconciliationRequired: z.boolean().optional(),
    resultCorrectionReason: z.string().max(500).optional(),
});
const MultipleAnswerResponseValue = z
    .object({
    kind: z.literal('multipleAnswer'),
    selectedOptionIds: z.array(z.string().min(1).max(8)).max(8),
})
    .superRefine((value, ctx) => {
    if (new Set(value.selectedOptionIds).size !== value.selectedOptionIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['selectedOptionIds'],
            message: 'selected option IDs must be unique',
        });
    }
});
const MatchingResponseValue = z.object({
    kind: z.literal('matching'),
    pairs: z.record(z.string().min(1).max(32), z.string().min(1).max(32)),
});
export const ESSAY_ATTACHMENT_MAX_FILES = 5;
export const ESSAY_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
/**
 * Formats the legacy cbt-web essay uploader allowed (application/controllers/
 * exam.php), plus `txt` which v2 already supported. Validation is by the final
 * lowercased extension only — browser-reported MIME types are unreliable for
 * formats like .rar and .mov. Extensions absent from this list (html, svg, js,
 * exe, php, ...) are rejected.
 */
export const ESSAY_ATTACHMENT_ALLOWED_EXTENSIONS = [
    'jpeg', 'jpg', 'png', 'gif',
    'doc', 'docx', 'pdf', 'zip',
    'xls', 'xlsx', 'ppt', 'pptx', 'ods', 'rar',
    'mp3', 'mov', 'mp4', 'txt',
];
/** Server-side extension → content type map used for the signed upload URL. */
export const ESSAY_ATTACHMENT_MIME_BY_EXTENSION = {
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pdf: 'application/pdf',
    zip: 'application/zip',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ods: 'application/vnd.oasis.opendocument.spreadsheet',
    rar: 'application/vnd.rar',
    mp3: 'audio/mpeg',
    mov: 'video/quicktime',
    mp4: 'video/mp4',
    txt: 'text/plain',
};
/**
 * The content type a file may be uploaded as, derived from its final
 * extension. Returns null when the name has no usable extension or the
 * extension is not in the allowlist.
 */
export function essayAttachmentMimeType(fileName) {
    const match = /\.([^.\\/]+)$/.exec(fileName.trim());
    const extension = match?.[1]?.toLowerCase();
    if (!extension)
        return null;
    return ESSAY_ATTACHMENT_MIME_BY_EXTENSION[extension] ?? null;
}
export const EssayAttachment = z.object({
    storagePath: z.string().min(1).max(512),
    fileName: z.string().min(1).max(200),
    mimeType: z.string().min(1).max(100),
    byteSize: z.number().int().positive().max(ESSAY_ATTACHMENT_MAX_BYTES),
});
export const ResponseValue = z.union([
    z.object({
        kind: z.literal('ordering'),
        order: z.array(z.string().min(1).max(32)).min(2).max(20).refine((order) => new Set(order).size === order.length, 'ordering item IDs must be unique'),
    }),
    z.object({ kind: z.literal('mcq'), selectedOptionId: z.string().min(1).max(8) }),
    MultipleAnswerResponseValue,
    z.object({ kind: z.literal('trueFalse'), value: z.boolean() }),
    z.object({ kind: z.literal('shortAnswer'), text: z.string().max(500) }),
    // Raw input is kept verbatim; comma/period decimals are normalized at grade
    // time so the stored value survives an audit read.
    z.object({ kind: z.literal('numeric'), text: z.string().max(64) }),
    MatchingResponseValue,
    z.object({
        kind: z.literal('essay'),
        text: z.string().max(8000),
        attachments: z.array(EssayAttachment).max(ESSAY_ATTACHMENT_MAX_FILES).optional(),
    }),
]);
export const Response = z.object({
    questionId: QuestionId,
    value: ResponseValue.optional(),
    answeredAtElapsedMs: z.number().int().nonnegative(), // monotonic; not wallclock
    isDoubtful: z.boolean().default(false),
    attempts: z.number().int().positive().default(1),
    revisedCount: z.number().int().nonnegative().default(0),
    seq: z.number().int().nonnegative(), // monotonic write sequence per session
    /**
     * Sequence of the last accepted write that CHANGED `value`. The no-redo
     * lock compares it against the page's commit sequence: an answer only
     * locks when a write landed on a later page after this value was saved.
     * Absent on responses written before the marker shipped — the API falls
     * back to `seq`.
     */
    valueSeq: z.number().int().nonnegative().optional(),
});
export const StudentResponse = Response.pick({
    questionId: true,
    value: true,
    answeredAtElapsedMs: true,
    isDoubtful: true,
    seq: true,
    valueSeq: true,
});
export const AuditEventType = z.enum([
    'visibility_hidden',
    'visibility_visible',
    'fullscreen_exit',
    'fullscreen_enter',
    // The automatic re-entry issued after a proctor unlock was refused by the
    // browser (no transient activation). Distinct from `fullscreen_exit`: the
    // student did not leave, the platform declined to put them back, so a
    // reviewer can tell "unlocked and re-entered" from "unlocked and never went
    // back". Recorded once per refused automatic attempt, never for a refusal of
    // the gate's own button (that is a student-visible error, not an event).
    'fullscreen_reentry_blocked',
    // The document's own <video> entering or leaving Picture-in-Picture. A PiP
    // window owned by another application is not observable from the web
    // platform, so only the page's own video can ever be reported.
    'picture_in_picture_enter',
    'picture_in_picture_leave',
    'split_screen', // viewport is a fraction of the display (multi-window)
    'devtools_open', // reserved; not enforced in MVP
    'paste',
    'time_drift',
    'reconnect',
    'proctor_command',
    'tab_blur',
    'tab_focus',
    // Sprint 4 additions
    'camera_consent_granted',
    'camera_consent_declined',
    // AIMSIS-20724 — a student may withdraw camera consent mid-attempt; the
    // stream stops and later captures are refused rather than silently skipped.
    'camera_consent_withdrawn',
    // AIMSIS-20724 — disclosed camera capture. One event per stage so a reader can
    // reconstruct who asked for whose still, when, and what the server did with it.
    'camera_capture_requested',
    'camera_capture_stored',
    'camera_capture_failed',
    'camera_capture_refused',
    'camera_capture_deleted',
    // A reviewer's disposition and an export are NOT an acceptance: reusing
    // `camera_capture_stored` for them made the trail unable to answer "who looked
    // at whose still", which is the only question the trail exists for.
    'camera_capture_reviewed',
    'camera_capture_exported',
    // AIMSIS-20932 — a proctor downloaded the violation log for this attempt.
    'violation_log_exported',
    'lockout_engaged', // session paused by client-side policy
    'lockout_released', // proctor unlocked a paused session
    'force_submit', // proctor or auto-policy submitted the session
    'policy_violation', // generic catch-all for rule trips
    'unlock_request_created',
    'unlock_request_waiting',
    'unlock_request_approved',
    'unlock_request_denied',
    'unlock_request_expired',
    'unlock_code_redeemed',
    'unlock_code_expired',
    'recovery_reopen',
    'recovery_reset',
    'recovery_reattempt',
]);
export const AuditEvent = z.object({
    id: z.string().min(1).max(64),
    at: IsoTimestamp,
    atElapsedMs: z.number().int().nonnegative().optional(),
    type: AuditEventType,
    serverStamped: z.boolean().default(false), // true if event timestamp set by server
    details: z.record(z.string(), z.unknown()).default({}),
});
// ---- API request/response schemas -------------------------------------------
export const SessionStartRequest = z.object({
    quizId: QuizId,
    passcode: z.union([z.string().regex(/^\d{6}$/), z.literal('')]).default(''),
    deviceFingerprint: z.string().max(256).optional(),
    clientTimeMs: z.number().int().nonnegative(),
});
// SessionStartResponse uses the allowlisted StudentQuestion schema. The API
// also constructs the payload field-by-field so grading fields cannot drift in.
//# sourceMappingURL=session.js.map